/**
 * REGIONS — управление регионами тура (Argentina/Chile/Antarctica/etc.).
 *
 * Phase 5 sub-extract (Sprint 15 C.3 part 1, 2026-05-10).
 * Извлечено из public/js/app.js.
 *
 * Регион — это namespace для каталога услуг и категорий. Каждый регион
 * хранится отдельным ключом в localStorage:
 *   • quoteCalc_regions_{orgId}        — список регионов
 *   • quoteCalc_currentRegion_{orgId}  — активный регион (Sprint 20: per-org)
 *   • quoteCalc_templates_{regionName} — каталог услуг
 *   • quoteCalc_categories_{regionName} — категории для региона
 *
 * Содержит:
 *   • initRegions / renderRegionSelector — bootstrap региональной системы
 *   • renderRegionsManager / selectRegionForView / loadCategoriesForRegionView
 *     — UI для управления списком регионов в Settings
 *   • startInlineRename / finishInlineRename — inline-rename из списка
 *   • addRegionFromManager / deleteRegion — CRUD операции
 *   • changeRegion — переключение активного региона (с save+load каталога)
 *
 * Подключение из entry.js через `attachRegions(ProfessionalQuoteCalculator)`.
 *
 * @module app/regions
 */

'use strict';

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

// Helper для построения per-org localStorage keys (Sprint 20 multi-tenancy).
function getOrgId() {
    return (window.__crmUser__ && window.__crmUser__.organization_id)
        || (window.apiClient && window.apiClient._currentOrgId)
        || 'unknown';
}

export function attachRegions(ClassRef) {
    Object.assign(ClassRef.prototype, {
        initRegions() {
            // B010 fix: per-org regions (multi-tenancy isolation).
            // Раньше использовался shared `quoteCalc_regions` → utечка management→customer.
            // Теперь key включает organization_id (или 'unknown' если ещё не loaded).
            const orgId = getOrgId();
            const REGIONS_KEY = `quoteCalc_regions_${orgId}`;
            const CURRENT_KEY = `quoteCalc_currentRegion_${orgId}`;

            // B010 fix: one-time cleanup legacy GLOBAL keys (leaked management→customer).
            // Не путать с per-org keys ниже — здесь явные literal без orgId substitution.
            try { localStorage.removeItem('quoteCalc_regions'); } catch (_) {}
            try { localStorage.removeItem('quoteCalc_currentRegion'); } catch (_) {}

            const savedRegions = localStorage.getItem(REGIONS_KEY);
            let needsMigration = false;

            if (savedRegions) {
                const parsed = JSON.parse(savedRegions);
                if (!parsed.length || (parsed.length === 1 && parsed[0] === 'Default')) {
                    needsMigration = true;
                } else {
                    this.regions = parsed;
                }
            } else {
                needsMigration = true;
            }

            if (needsMigration) {
                // Default — generic, чтобы новый owner не получил Magellania-specific регионы.
                // Owner может добавить свои через Settings.
                this.regions = ['По умолчанию'];
                localStorage.setItem(REGIONS_KEY, JSON.stringify(this.regions));
                console.log('[regions] инициализирован per-org regions для', orgId);
            }

            const savedCurrent = localStorage.getItem(CURRENT_KEY);
            if (savedCurrent && this.regions.includes(savedCurrent)) {
                this.currentRegion = savedCurrent;
            } else {
                this.currentRegion = this.regions[0];
                localStorage.setItem(CURRENT_KEY, this.currentRegion);
            }

            console.log('Регионы инициализированы:', this.regions, 'Текущий:', this.currentRegion);

            // Рендерим селектор регионов
            this.renderRegionSelector();

            // Загружаем каталог для текущего региона
            this.loadCatalogForRegion(this.currentRegion);

            // 2026-05-16 fix: server-side regions sync. Before this, legacy
            // calculator + Settings → Manage regions read EXCLUSIVELY from
            // localStorage. New orgs / fresh browsers / superadmin saw the
            // default ['По умолчанию'] even though the server had real
            // regions (Buenos Aires, El Calafate, Ushuaia, etc. in catalogs.region).
            // Now: after sync default is set up, fetch from server in background
            // and merge — server is authoritative for the LIST, localStorage
            // remains a cache + holds currentRegion preference.
            this.syncRegionsFromServer();
        },

        /**
         * 2026-05-16 — pull canonical regions list from server (catalogs.region
         * distinct values) and overwrite the local list if the server has more.
         * Server == authoritative. Falls back silently when apiClient is missing
         * or the call fails (test pages, offline, pre-auth).
         */
        async syncRegionsFromServer() {
            if (!window.apiClient || typeof window.apiClient.listRegions !== 'function') return;
            try {
                const serverRegions = await window.apiClient.listRegions();
                if (!Array.isArray(serverRegions) || serverRegions.length === 0) {
                    // Server has no catalogs yet — keep local default.
                    return;
                }
                const orgId = getOrgId();
                const REGIONS_KEY = `quoteCalc_regions_${orgId}`;
                const CURRENT_KEY = `quoteCalc_currentRegion_${orgId}`;

                // Only overwrite if the server list differs (avoid pointless re-renders).
                const sameSet = this.regions.length === serverRegions.length
                    && serverRegions.every((r) => this.regions.includes(r));
                if (sameSet) return;

                this.regions = serverRegions.slice();
                localStorage.setItem(REGIONS_KEY, JSON.stringify(this.regions));

                // If currentRegion no longer exists in the server list — pick first.
                if (!this.regions.includes(this.currentRegion)) {
                    this.currentRegion = this.regions[0];
                    localStorage.setItem(CURRENT_KEY, this.currentRegion);
                }

                console.log('[regions] синхронизировано с сервером:', this.regions);

                // Re-render UI surfaces that may already be visible
                try { this.renderRegionSelector(); } catch (_) {}
                try { this.renderRegionsManager(); } catch (_) {}
                try { this.loadCatalogForRegion(this.currentRegion); } catch (_) {}
            } catch (err) {
                console.warn('[regions] sync from server failed (keeping local list):', err.message);
            }
        },

        renderRegionSelector() {
            const selector = document.getElementById('region-selector');
            if (!selector) return;

            selector.innerHTML = this.regions.map(region =>
                `<option value="${this.escapeHtml(region)}">${this.escapeHtml(region)}</option>`
            ).join('');
            selector.value = this.currentRegion;
        },

        renderRegionsManager() {
            const container = document.getElementById('regions-manager-list');
            if (!container) return;

            // Сохраняем выбранный регион для просмотра категорий
            if (!this.selectedRegionForView) {
                this.selectedRegionForView = this.currentRegion;
            }

            container.innerHTML = this.regions.map((region, index) => {
                const isActive = region === this.currentRegion;
                const isSelected = region === this.selectedRegionForView;
                const activeBadge = isActive ? `<span style="font-size: 10px; color: #f59e0b; background: #fef3c7; padding: 2px 6px; border-radius: 3px; margin-left: 6px; font-weight: 600;">${_t('app.region.active_badge', 'active')}</span>` : '';
                const canDelete = this.regions.length > 1;

                return `
                    <div class="region-manager-item ${isSelected ? 'region-item-selected' : ''}"
                         data-region="${this.escapeHtml(region)}"
                         onclick="window.QuoteCalc.selectRegionForView('${this.escapeHtml(region)}')"
                         style="display: flex; justify-content: space-between; align-items: center; padding: 12px; border-bottom: 1px solid #e5e7eb; background: ${isActive ? '#fffbeb' : 'white'}; transition: all 0.2s; cursor: pointer;">
                        <div class="region-inline-edit" style="display: flex; align-items: center; gap: 8px;">
                            <i data-lucide="map-pin" style="width: 14px; height: 14px; color: ${isActive ? '#92400e' : '#6b7280'};"></i>
                            <span class="region-name" style="font-weight: ${isActive ? '600' : '400'}; color: ${isActive ? '#92400e' : '#374151'};">${this.escapeHtml(region)}</span>
                            ${activeBadge}
                        </div>
                        <div style="display: flex; align-items: center; gap: 6px;">
                            <button onclick="event.stopPropagation(); window.QuoteCalc.startInlineRename('${this.escapeHtml(region)}')"
                                    style="width: 28px; height: 28px; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; border-radius: 4px; transition: all 0.15s;"
                                    onmouseover="this.style.background='#f3f4f6'; this.style.color='#4b5563';"
                                    onmouseout="this.style.background='transparent'; this.style.color='#9ca3af';"
                                    title="${_t('app.region.rename_title', 'Rename region')}"><i data-lucide="edit" style="width: 12px; height: 12px;"></i></button>
                            ${canDelete ? `
                                <button onclick="event.stopPropagation(); window.QuoteCalc.deleteRegion('${this.escapeHtml(region)}')"
                                        style="width: 28px; height: 28px; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; border-radius: 4px; transition: all 0.15s;"
                                        onmouseover="this.style.background='#fee2e2'; this.style.color='#dc2626';"
                                        onmouseout="this.style.background='transparent'; this.style.color='#9ca3af';"
                                        title="${_t('app.region.delete_title', 'Delete region')}">×</button>
                            ` : '<div style="width: 28px;"></div>'}
                        </div>
                    </div>
                `;
            }).join('');

            // Загружаем категории для выбранного региона
            this.loadCategoriesForRegionView(this.selectedRegionForView);
        },

        selectRegionForView(region) {
            this.selectedRegionForView = region;
            this.renderRegionsManager();
        },

        loadCategoriesForRegionView(region) {
            // Загружаем категории выбранного региона в правую панель
            const categoriesKey = `quoteCalc_categories_${region}`;
            const savedCategories = localStorage.getItem(categoriesKey);

            let categories = [];
            if (savedCategories) {
                categories = JSON.parse(savedCategories);
            } else {
                // Используем дефолтные категории
                categories = [
                    { id: 'undefined', name: _t('app.region.category_undefined', 'Undefined'), icon: '❓', system: true },
                    { id: 'transfer', name: _t('app.region.category_transfer', 'Transfer'), icon: '🚗' },
                    { id: 'accommodation', name: _t('app.region.category_accommodation', 'Hotels'), icon: '🏨' },
                    { id: 'guide', name: _t('app.region.category_guide', 'Guides'), icon: '👨‍🏫' },
                    { id: 'activity', name: _t('app.region.category_activity', 'Activities'), icon: '🏛️' },
                    { id: 'service', name: _t('app.region.category_service', 'Service'), icon: '🔧' }
                ];
            }

            // Временно сохраняем текущие категории
            const tempCategories = this.categories;
            this.categories = categories;

            // Рендерим список категорий
            this.renderCategoriesManager();

            // Восстанавливаем текущие категории
            this.categories = tempCategories;

            // Обновляем заголовок списка категорий
            const categoriesList = document.querySelector('#categories-manager-list');
            if (categoriesList && categoriesList.previousElementSibling) {
                const titleDiv = categoriesList.previousElementSibling;
                if (titleDiv.classList && titleDiv.classList.contains('text-sm')) {
                    const manageTitle = _t('app.region.manage_title', 'Manage categories');
                    const regionLabel = _t('app.region.manage_for_region', 'Region: {region}').replace('{region}', region);
                    titleDiv.innerHTML = `${manageTitle}<div style="font-size: 11px; color: var(--gray-500); font-weight: 400; margin-top: 4px;">${regionLabel}</div>`;
                }
            }
        },

        startInlineRename(oldName) {
            const items = document.querySelectorAll('.region-manager-item');
            items.forEach(item => {
                if (item.dataset.region === oldName) {
                    item.classList.add('editing');
                    const nameSpan = item.querySelector('.region-name');
                    const currentName = nameSpan.textContent;

                    const input = document.createElement('input');
                    input.type = 'text';
                    input.value = currentName;
                    input.className = 'region-inline-input';

                    // Сохранение по Enter
                    input.onkeydown = (e) => {
                        if (e.key === 'Enter') {
                            this.finishInlineRename(oldName, input.value.trim());
                        } else if (e.key === 'Escape') {
                            this.renderRegionsManager();
                        }
                    };

                    // Сохранение при потере фокуса
                    input.onblur = () => {
                        setTimeout(() => {
                            this.finishInlineRename(oldName, input.value.trim());
                        }, 200);
                    };

                    nameSpan.replaceWith(input);
                    input.focus();
                    input.select();
                }
            });
        },

        finishInlineRename(oldName, newName) {
            if (!newName || newName === oldName) {
                this.renderRegionsManager();
                return;
            }

            if (this.regions.includes(newName)) {
                this.showNotification(_t('app.region.exists_error', 'A region with this name already exists'), true);
                this.renderRegionsManager();
                return;
            }

            // Переименовываем регион
            const index = this.regions.indexOf(oldName);
            if (index !== -1) {
                this.regions[index] = newName;
                localStorage.setItem(`quoteCalc_regions_${getOrgId()}`, JSON.stringify(this.regions));

                // Переименовываем ключи в localStorage
                const oldCatalogKey = `quoteCalc_templates_${oldName}`;
                const newCatalogKey = `quoteCalc_templates_${newName}`;
                const oldCategoriesKey = `quoteCalc_categories_${oldName}`;
                const newCategoriesKey = `quoteCalc_categories_${newName}`;

                const catalog = localStorage.getItem(oldCatalogKey);
                const categories = localStorage.getItem(oldCategoriesKey);

                if (catalog) {
                    localStorage.setItem(newCatalogKey, catalog);
                    localStorage.removeItem(oldCatalogKey);
                }

                if (categories) {
                    localStorage.setItem(newCategoriesKey, categories);
                    localStorage.removeItem(oldCategoriesKey);
                }

                // Обновляем текущий регион если нужно
                if (this.currentRegion === oldName) {
                    this.currentRegion = newName;
                    localStorage.setItem(`quoteCalc_currentRegion_${getOrgId()}`, newName);
                }

                // Обновляем выбранный регион для просмотра
                if (this.selectedRegionForView === oldName) {
                    this.selectedRegionForView = newName;
                }

                this.renderRegionSelector();
                this.renderRegionsManager();
                this.showNotification(
                    _t('app.region.renamed', 'Region renamed: "{old}" → "{new}"')
                        .replace('{old}', oldName)
                        .replace('{new}', newName)
                );
            }
        },

        addRegionFromManager() {
            const input = document.getElementById('region-manager-name');
            if (!input) return;

            const name = input.value.trim();
            if (!name) {
                this.showNotification(_t('app.region.enter_name', 'Enter region name'), true);
                return;
            }

            if (this.regions.includes(name)) {
                this.showNotification(_t('app.region.exists_error', 'A region with this name already exists'), true);
                return;
            }

            // Добавляем регион
            this.regions.push(name);
            localStorage.setItem(`quoteCalc_regions_${getOrgId()}`, JSON.stringify(this.regions));

            // Создаём пустой каталог для нового региона
            localStorage.setItem(`quoteCalc_templates_${name}`, JSON.stringify([]));

            // Копируем категории текущего региона в новый (или используем дефолтные)
            let newRegionCategories = JSON.parse(localStorage.getItem(`quoteCalc_categories_${this.currentRegion}`) || 'null');

            if (!newRegionCategories) {
                // Если нет категорий в текущем регионе - используем дефолтные
                newRegionCategories = [
                    { id: 'undefined', name: _t('app.region.category_undefined', 'Undefined'), icon: '❓', system: true },
                    { id: 'transfer', name: _t('app.region.category_transfer', 'Transfer'), icon: '🚗' },
                    { id: 'accommodation', name: _t('app.region.category_accommodation', 'Hotels'), icon: '🏨' },
                    { id: 'guide', name: _t('app.region.category_guide', 'Guides'), icon: '👨‍🏫' },
                    { id: 'activity', name: _t('app.region.category_activity', 'Activities'), icon: '🏛️' },
                    { id: 'service', name: _t('app.region.category_service', 'Service'), icon: '🔧' }
                ];
            } else {
                // ВАЖНО: Гарантируем наличие системной категории "Не определено"
                const hasUndefinedCategory = newRegionCategories.some(cat => cat.id === 'undefined');
                if (!hasUndefinedCategory) {
                    console.log(`Добавляем системную категорию "Не определено" в новый регион "${name}"`);
                    newRegionCategories.unshift({
                        id: 'undefined',
                        name: _t('app.region.category_undefined', 'Undefined'),
                        icon: '❓',
                        system: true
                    });
                }
            }

            localStorage.setItem(`quoteCalc_categories_${name}`, JSON.stringify(newRegionCategories));

            input.value = '';
            this.renderRegionSelector();
            this.renderRegionsManager();
            this.showNotification(
                _t('app.region.added', 'Region "{name}" added').replace('{name}', name)
            );
        },

        async deleteRegion(regionName) {
            if (this.regions.length === 1) {
                this.showNotification(_t('app.region.cannot_delete_last', 'Cannot delete the last region'), true);
                return;
            }

            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.region.delete_confirm_title', 'Delete region?'),
                message: _t('app.region.delete_confirm_message', 'Delete region "{name}"? All services and categories in this region will be removed.')
                    .replace('{name}', regionName),
                danger: true
            });
            if (!ok) return;

            // Удаляем регион из списка
            this.regions = this.regions.filter(r => r !== regionName);
            localStorage.setItem(`quoteCalc_regions_${getOrgId()}`, JSON.stringify(this.regions));

            // Удаляем данные региона
            localStorage.removeItem(`quoteCalc_templates_${regionName}`);
            localStorage.removeItem(`quoteCalc_categories_${regionName}`);

            // Если удалили текущий регион - переключаемся на первый доступный
            if (this.currentRegion === regionName) {
                this.currentRegion = this.regions[0];
                localStorage.setItem(`quoteCalc_currentRegion_${getOrgId()}`, this.currentRegion);
                this.loadCatalogForRegion(this.currentRegion);
            }

            this.renderRegionSelector();
            this.renderRegionsManager();
            this.showNotification(
                _t('app.region.deleted', 'Region "{name}" deleted').replace('{name}', regionName)
            );
        },

        changeRegion(newRegion) {
            if (newRegion === this.currentRegion) return;

            try {
                // Сохраняем текущий каталог перед переключением
                this.saveCatalogToRegion(this.currentRegion);

                // Переключаемся на новый регион
                this.currentRegion = newRegion;
                localStorage.setItem(`quoteCalc_currentRegion_${getOrgId()}`, newRegion);

                // Загружаем каталог нового региона
                this.loadCatalogForRegion(newRegion);

                this.showNotification(
                    _t('app.region.switched', 'Switched to region: {name}').replace('{name}', newRegion)
                );
            } catch (error) {
                console.error('Ошибка переключения региона:', error);
                this.showNotification(
                    _t('app.region.switch_error', 'Region switch failed: {error}').replace('{error}', error.message),
                    true
                );
            }
        }
    });
}
